import React, { useCallback, useEffect, useMemo } from 'react'
import { Flex, Form } from 'antd'
import SelectRange from 'src/components/common/selectRange/SelectRange'
import SelectMultiple from 'src/components/common/selectMultiple/SelectMultiple'
import styles from './FilterSection.module.less'
import Select from 'src/components/common/select/Select'
import { UIButton } from 'src/components/common/uiButton/UIButton'
import Tag from 'src/components/common/tag/Tag'
import {
    FiltersLabelMapping,
    FiltersKeyType,
    LifeCycleStageColorMapping,
    OptionType,
    FilterOptionRespType,
    FiltersValue,
} from 'src/types/expert.types'
import { FilteredTag } from 'src/hooks/useTag'
import SearchBox from 'src/components/common/searchBox/SearchBox'
import { DataTestId, TextConstants } from 'src/constants/ui.constants'
import { YearsOfExperienceRange } from 'src/constants/experts.constants'
import SegmentAnalytics from 'src/SegmentAnalytics'

const {
    SearchByNameEmail,
    SearchByExpertiseArea,
    ByLocation,
    ByOccupation,
    ByEducationDegree,
    ByEducationMajor,
    ByYearsOfExperience,
    ByLanguages,
    ByLifeCycleStage,
    ApplyFilters,
} = TextConstants.FilterSection

export type FilterSectionProps = {
    removedTagName: string
    updateTags: (newTags: FilteredTag[]) => void
    filterOption: FilterOptionRespType | undefined
}

type MultipleType = string | [number, number] | string[]

type SelectedFilterType = string | [number, number] | undefined

type FilteredObjectItem = {
    id: string | string[] | undefined
    key: string
    value: string
}

export const FilterSection: React.FC<FilterSectionProps> = props => {
    const { removedTagName, updateTags, filterOption } = props
    const [form] = Form.useForm()
    const formValues: Record<string, MultipleType> = Form.useWatch([], form)
    const isFormEmpty = useMemo(() => {
        return (
            !formValues ||
            Object.values(formValues).every(value => value === undefined || value.length === 0)
        )
    }, [formValues])

    const handleSubmit = () => {
        const getId = (key: string, value: MultipleType) => {
            if (Array.isArray(value)) {
                const result = value.map(id => {
                    return (
                        filterOption &&
                        filterOption[key as keyof FilterOptionRespType]?.find(
                            val => val.value === id
                        )?.id
                    )
                })
                const filteredResult = result.filter((val): val is string => val !== undefined)
                return filteredResult.length > 0 ? filteredResult : undefined
            }
            return (
                (filterOption && filterOption[key as keyof FilterOptionRespType])?.find(
                    val => val.value === value
                )?.id ?? undefined
            )
        }

        const getValue = (key: string, value: MultipleType) => {
            if (key === FiltersValue.YearsOfExperience) {
                return `${value[0]}-${value[1]}`
            } else if (Array.isArray(value)) {
                return value.join(', ')
            }
            return value
        }

        const filteredObject = Object.entries(formValues)
            .filter(([, value]) => !(value === undefined || value.length === 0))
            .map(([key, value]) => {
                return {
                    id: getId(key, value),
                    key: FiltersLabelMapping[key] || key,
                    value: getValue(key, value),
                }
            })

        const selectedFilters = filteredObject.reduce(
            (acc: Record<string, string>, val: FilteredObjectItem) => {
                acc[val.key] = val.value
                return acc
            },
            {}
        )

        SegmentAnalytics.sendTrackEvent(`FilterSection_ApplyFilterCTA_Clicked`, {
            selectedFilters,
        })

        form.resetFields([FiltersValue.ExpertiseArea])
        updateTags(filteredObject)
    }

    useEffect(() => {
        if (removedTagName) {
            if (removedTagName === FiltersKeyType.SelectAll) {
                form.resetFields()
                return
            }
            const key = Object.keys(FiltersLabelMapping).find(
                key => FiltersLabelMapping[key] === removedTagName
            )
            if (key) {
                form.resetFields([key])
            }
        }
    }, [form, removedTagName])

    const handleOnSelect = useCallback(
        (filterKey: string, selectedFilterValue: SelectedFilterType) => {
            if (selectedFilterValue) {
                SegmentAnalytics.sendTrackEvent(`FilterSection_Select_${filterKey}_dropdown`, {
                    selectedValue: selectedFilterValue,
                })
            }
        },
        []
    )

    const handleOnSearch = useCallback((filterKey: string, selectedFilterValue: string) => {
        if (selectedFilterValue) {
            SegmentAnalytics.sendTrackEvent(`FilterSection_${filterKey}_Search`, {
                searchValue: selectedFilterValue,
            })
        }
    }, [])

    const handleOnDropdownVisibleChange = useCallback(
        (filterKey: string, dropdownVisibilityStatus: boolean) => {
            if (filterKey === FiltersValue.Language && !dropdownVisibilityStatus) {
                SegmentAnalytics.sendTrackEvent(
                    `FilterSection_Select_${filterKey}_dropdown`,
                    {},
                    filterKey
                )
            }
        },
        []
    )

    const handleOnChangeForMultiSelect = useCallback((selectedFilterValue: string[]) => {
        if (selectedFilterValue?.length) {
            SegmentAnalytics.appendEventData({
                selectedValues: [...selectedFilterValue],
            })
        }
    }, [])

    return (
        <Form
            form={form}
            className={styles.formStyle}
            onFinish={handleSubmit}
            data-testid={DataTestId.FilterSection}
        >
            <Flex gap={'10px'} className={styles.filterRow} wrap>
                <Form.Item name={FiltersValue.NameAndEmail}>
                    <SearchBox
                        placeholder={SearchByNameEmail}
                        width={'260px'}
                        onPressEnter={value => handleOnSearch(FiltersValue.NameAndEmail, value)}
                    />
                </Form.Item>
                <Form.Item name={FiltersValue.ExpertiseArea}>
                    <SearchBox
                        placeholder={SearchByExpertiseArea}
                        width={'235px'}
                        onPressEnter={value => handleOnSearch(FiltersValue.ExpertiseArea, value)}
                    />
                </Form.Item>
                <Form.Item name={FiltersValue.Occupation}>
                    <Select
                        options={filterOption?.occupation}
                        placeholder={ByOccupation}
                        width={'180px'}
                        allowClear
                        onChange={value => handleOnSelect(FiltersValue.Occupation, value)}
                    />
                </Form.Item>
                <Form.Item name={FiltersValue.EducationDegree}>
                    <Select
                        options={filterOption?.educationDegree}
                        placeholder={ByEducationDegree}
                        width={'210px'}
                        allowClear
                        onChange={value => handleOnSelect(FiltersValue.EducationDegree, value)}
                    />
                </Form.Item>
                <Form.Item name={FiltersValue.EducationMajor}>
                    <Select
                        options={filterOption?.educationMajor}
                        placeholder={ByEducationMajor}
                        width={'180px'}
                        allowClear
                        onChange={value => handleOnSelect(FiltersValue.EducationMajor, value)}
                    />
                </Form.Item>
                <Form.Item name={FiltersValue.Location}>
                    <Select
                        options={filterOption?.location}
                        placeholder={ByLocation}
                        width={'160px'}
                        allowClear
                        onChange={value => handleOnSelect(FiltersValue.Location, value)}
                    />
                </Form.Item>
                <Form.Item name={FiltersValue.YearsOfExperience}>
                    <SelectRange
                        max={YearsOfExperienceRange.max}
                        min={YearsOfExperienceRange.min}
                        placeholder={ByYearsOfExperience}
                        width={'200px'}
                        allowClear
                        minLimit={0}
                        maxLimit={8}
                        onChange={value => handleOnSelect(FiltersValue.YearsOfExperience, value)}
                    />
                </Form.Item>
                <Form.Item name={FiltersValue.Language}>
                    <SelectMultiple
                        options={filterOption?.language}
                        placeholder={ByLanguages}
                        maxTextDisplayLength={26}
                        width={'180px'}
                        onChange={value => handleOnChangeForMultiSelect(value)}
                        onDropdownVisibleChange={visible =>
                            handleOnDropdownVisibleChange(FiltersValue.Language, visible)
                        }
                    />
                </Form.Item>
                <Form.Item name={FiltersValue.LifeCycleStage}>
                    <Select
                        options={filterOption?.lifeCycleStage.map((lifeCycleStage: OptionType) => ({
                            ...lifeCycleStage,
                            label: (
                                <Tag
                                    label={lifeCycleStage.label}
                                    color={LifeCycleStageColorMapping[lifeCycleStage.value]}
                                    isUppercase
                                    fitWidth
                                />
                            ),
                        }))}
                        placeholder={ByLifeCycleStage}
                        width={'190px'}
                        allowClear
                        onChange={value => handleOnSelect(FiltersValue.LifeCycleStage, value)}
                    />
                </Form.Item>
                <Form.Item name="button">
                    <UIButton
                        type={'primary'}
                        htmlType="submit"
                        title={ApplyFilters}
                        isDisabled={isFormEmpty}
                    />
                </Form.Item>
            </Flex>
        </Form>
    )
}
